import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Quote, Star } from 'lucide-react';
import { mockData } from '../data/mockData';
import { contactWhatsApp } from '../utils/buttonActions';
import SectionHeader from './SectionHeader';
import IndustrialButton from './IndustrialButton';

const AVATAR_COLORS = ['#FF2A00', '#0A0A0A', '#2A6FBB', '#1F8A5B'];

const getInitials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const getCardOffset = (index, activeIndex, total) => {
  const rawOffset = index - activeIndex;
  const half = total / 2;

  if (rawOffset > half) {
    return rawOffset - total;
  }

  if (rawOffset < -half) {
    return rawOffset + total;
  }

  return rawOffset;
};

const renderStars = (rating) =>
  Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      className={`h-4 w-4 ${index < rating ? 'fill-[#FF2A00] text-[#FF2A00]' : 'text-[#777]'}`}
      strokeWidth={2}
    />
  ));

function TestimonialStackCard({ testimonial, index, offset, isActive, onSelect }) {
  const clampedOffset = Math.max(-2, Math.min(2, offset));
  const depth = Math.abs(clampedOffset);
  const x = clampedOffset * 34;
  const y = depth * 18;
  const scale = 1 - depth * 0.055;
  const rotate = clampedOffset * -5;
  const zIndex = 10 - depth;
  const opacity = depth > 2 ? 0 : 1 - depth * 0.14;
  const accent = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <button
      type="button"
      className={`testimonial-stack-card ${isActive ? 'is-active' : ''}`}
      onClick={onSelect}
      aria-label={`Show testimonial from ${testimonial.name}`}
      aria-pressed={isActive}
      style={{
        '--card-x': `${x}px`,
        '--card-y': `${y}px`,
        '--card-scale': scale,
        '--card-rotate': `${rotate}deg`,
        '--card-opacity': opacity,
        '--card-z': zIndex,
        '--avatar-color': accent,
      }}
      data-testid={`testimonial-stack-card-${index}`}
    >
      <div className="testimonial-stack-card-inner">
        <div className="flex items-start justify-between gap-4">
          <div className="testimonial-avatar" aria-hidden="true">
            {getInitials(testimonial.name)}
          </div>
          <Quote className="h-9 w-9 text-[#FF2A00]" strokeWidth={2} />
        </div>

        <div className="mt-7 flex gap-1" aria-label={`${testimonial.rating} star rating`}>
          {renderStars(testimonial.rating)}
        </div>

        <blockquote className="mt-5">
          "{testimonial.text}"
        </blockquote>

        <div className="mt-8 flex items-end justify-between gap-5 border-t-2 border-[#0A0A0A] pt-5">
          <div>
            <h3>{testimonial.name}</h3>
            <p>{testimonial.designation}, {testimonial.company}</p>
          </div>
          <span>{testimonial.location}</span>
        </div>
      </div>
    </button>
  );
}

const Testimonials = () => {
  const testimonials = mockData.testimonials;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(null);

  const nextTestimonial = () => {
    setActiveIndex((current) => (current + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveIndex((current) => (current - 1 + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    if (isPaused) {
      return undefined;
    }

    const interval = setInterval(nextTestimonial, 4800);
    return () => clearInterval(interval);
  }, [isPaused, testimonials.length]);

  const handleTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX.current === null) {
      return;
    }

    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX < 0) {
      nextTestimonial();
    } else {
      prevTestimonial();
    }
  };

  return (
    <section className="testimonial-stack-section py-20 lg:py-28 bg-white border-b-2 border-[#0A0A0A] overflow-hidden" data-testid="testimonials-section">
      <div className="testimonial-marquee" aria-hidden="true">
        <span>READABLE FROM DISTANCE / BUILT TO LAST / INSTALLED CLEAN / </span>
        <span>READABLE FROM DISTANCE / BUILT TO LAST / INSTALLED CLEAN / </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-center">
          <div>
            <SectionHeader
              eyebrow="Client Signal"
              title="What Clients"
              accent="Say"
              copy="Straightforward feedback from businesses who needed signage done clearly, quickly, and properly."
            />

            <div className="testimonial-stack-copy" data-reveal>
              <strong>{String(activeIndex + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}</strong>
              <p>{testimonials[activeIndex].company} trusted AIM Flex for visible, durable, production-ready signage.</p>
            </div>

            <div className="testimonial-stack-actions" data-reveal>
              <button
                type="button"
                onClick={() => { setIsPaused(true); prevTestimonial(); }}
                className="testimonial-stack-nav"
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
              >
                <ArrowLeft className="h-5 w-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => { setIsPaused(true); nextTestimonial(); }}
                className="testimonial-stack-nav"
                aria-label="Next testimonial"
                data-testid="testimonial-next"
              >
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div
            className="testimonial-stack-stage"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            data-testid="testimonial-stack-stage"
            data-reveal
          >
            <div className="testimonial-stack-perspective">
              {testimonials.map((testimonial, index) => (
                <TestimonialStackCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  index={index}
                  offset={getCardOffset(index, activeIndex, testimonials.length)}
                  isActive={index === activeIndex}
                  onSelect={() => { setIsPaused(true); setActiveIndex(index); }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mt-14" data-reveal>
          <IndustrialButton
            onClick={() => contactWhatsApp('Hi! After reading client reviews, I would like to get a quote for my project.')}
            testId="testimonial-cta"
          >
            Get Your Quote Today
          </IndustrialButton>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
